import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Loader2, Share, Plus, MoreVertical, Smartphone, CheckCircle2 } from "lucide-react";
import { SportIcon } from "@/components/SportIcon";
import { enableRunnerPush, disableRunnerPush, getRunnerNotifications, saveRunnerSports, pushSupported, RUNNER_CATEGORIES } from "@/lib/push";
import { isStandalone, getPlatform, iosPushCapable, getBrowser } from "@/lib/device";

export function NotifyOnboarding() {
  const [notif, setNotif] = useState({ sports: [], subscribed: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => { getRunnerNotifications().then(setNotif).catch(() => {}); }, []);

  const toggle = (slug) => setNotif((n) => ({ ...n, sports: n.sports.includes(slug) ? n.sports.filter((x) => x !== slug) : [...n.sports, slug] }));

  const standalone = isStandalone();
  const platform = getPlatform();
  const supported = pushSupported();
  const iosOk = iosPushCapable();
  const browser = getBrowser();

  const save = async () => { setBusy(true); try { await saveRunnerSports(notif.sports); toast.success("Preferences saved"); } catch { toast.error("Could not save"); } finally { setBusy(false); } };
  const enable = async () => { setBusy(true); try { await enableRunnerPush(notif.sports.length ? notif.sports : RUNNER_CATEGORIES.map((c) => c.slug)); setNotif(await getRunnerNotifications()); toast.success("Notifications enabled"); } catch (e) { toast.error(e.message || "Could not enable"); } finally { setBusy(false); } };
  const disable = async () => { setBusy(true); try { await disableRunnerPush(); setNotif((n) => ({ ...n, subscribed: false })); toast.success("Notifications disabled"); } finally { setBusy(false); } };

  // iOS: push only works when installed (standalone) AND on iOS 16.4+. Android/desktop can enable in a supported browser (install still encouraged).
  const iosBlockedVersion = platform === "ios" && standalone && !iosOk;
  const canEnable = supported && !iosBlockedVersion && (standalone || platform === "android" || platform === "desktop");
  const needInstall = !standalone && (platform === "ios" || platform === "android");

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6" data-testid="runner-notifications">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-lg bg-cyan-100 text-[#06b6d4] grid place-items-center shrink-0"><Bell size={18} /></span>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold uppercase text-lg text-slate-800">New Race Notifications</div>
          <div className="text-sm text-slate-500 mb-4">Free, optional phone alerts when a new race in your chosen sports is published — each opens the race page on YourRaceFinder. {notif.subscribed && <span className="font-semibold text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 size={14} /> Enabled on this device</span>}</div>

          {/* Category chips */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="notif-sport-list">
            {RUNNER_CATEGORIES.map((c) => (
              <button key={c.slug} onClick={() => toggle(c.slug)} data-testid={`notif-sport-${c.slug}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${notif.sports.includes(c.slug) ? "bg-[#0a1420] text-white border-[#0a1420]" : "border-slate-300 text-slate-600"}`}>
                <SportIcon sport={c.slug} size={15} />{c.name}</button>
            ))}
          </div>

          {iosBlockedVersion && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3" data-testid="notif-ios-version">
              Push notifications on iPhone/iPad require iOS/iPadOS 16.4 or later. Please update iOS to enable alerts — you can still browse and save races.
            </p>
          )}

          {needInstall && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4" data-testid="notif-install-guide">
              <div className="font-semibold text-slate-800 text-sm flex items-center gap-1.5 mb-2"><Smartphone size={15} className="text-[#06b6d4]" /> Add YourRaceFinder to your Home Screen</div>
              {platform === "ios" ? (
                <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside" data-testid="install-steps-ios">
                  <li>Open this site in <b>Safari</b> (required on iPhone/iPad).</li>
                  <li>Tap the <b>Share</b> icon <Share size={13} className="inline -mt-0.5" /> at the bottom of the screen.</li>
                  <li>Choose <b>Add to Home Screen</b> <Plus size={13} className="inline -mt-0.5" />, then tap <b>Add</b>.</li>
                  <li>Open <b>YourRaceFinder</b> from your Home Screen, then tap <b>Enable Notifications</b> below.</li>
                </ol>
              ) : (
                <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside" data-testid="install-steps-android">
                  <li>In <b>Chrome</b>, tap the <b>⋮</b> menu <MoreVertical size={13} className="inline -mt-0.5" /> (top-right).</li>
                  <li>Tap <b>Install app</b> or <b>Add to Home screen</b>, then confirm.</li>
                  <li>Open <b>YourRaceFinder</b> from your Home Screen, then tap <b>Enable Notifications</b> below.</li>
                </ol>
              )}
              {platform === "ios" && <p className="text-xs text-slate-400 mt-2">Requires iOS/iPadOS 16.4 or later.</p>}
              {platform === "android" && !["chrome", "samsung", "edge"].includes(browser) && <p className="text-xs text-slate-400 mt-2">Tip: Chrome gives the most reliable install &amp; notification experience on Android.</p>}
            </div>
          )}

          {!needInstall && !standalone && (platform === "android" || platform === "desktop") && (
            <p className="text-xs text-slate-400 mb-3">Tip: add YourRaceFinder to your Home Screen for the best, most reliable notification experience.</p>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            {canEnable ? (
              notif.subscribed ? (
                <>
                  <button onClick={save} disabled={busy} data-testid="notif-save" className="inline-flex items-center gap-2 bg-[#22d3ee] text-[#0a1420] font-bold px-5 py-2.5 rounded-lg hover:bg-[#06b6d4] disabled:opacity-50">{busy && <Loader2 size={15} className="animate-spin" />} Save Preferences</button>
                  <button onClick={disable} disabled={busy} data-testid="notif-disable" className="text-sm font-semibold text-slate-500 hover:text-slate-800 px-3 py-2">Turn off</button>
                </>
              ) : (
                <button onClick={enable} disabled={busy} data-testid="notif-enable" className="inline-flex items-center gap-2 bg-[#0a1420] text-white font-bold px-5 py-2.5 rounded-lg hover:bg-[#1a2740] disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : <Bell size={15} />} Enable Notifications</button>
              )
            ) : (
              <button onClick={save} disabled={busy} data-testid="notif-save-prefs" className="inline-flex items-center gap-2 bg-[#22d3ee] text-[#0a1420] font-bold px-5 py-2.5 rounded-lg hover:bg-[#06b6d4] disabled:opacity-50">{busy && <Loader2 size={15} className="animate-spin" />} Save My Categories</button>
            )}
          </div>
          {!canEnable && !needInstall && !iosBlockedVersion && !supported && (
            <p className="text-xs text-slate-400 mt-3" data-testid="notif-unsupported">This browser doesn't support push notifications. Your categories are saved and you can enable alerts from a supported device.</p>
          )}
        </div>
      </div>
    </div>
  );
}
